import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Structure } from "@/types/designs";
import { DesignTableCell } from "../DesignTableCell";
import { parseSelections } from "../../DesignUploadStep.functions";
import { PortComboSortKey } from "../../DesignUploadStep.values";
import { useStructureTableSort } from "../../hooks/useStructureTableSort";
import { SortIcon } from "../icons/SortIcon";

type PortComboDraft = NonNullable<Structure["portCombos"]>[number];

type PortCombosTableProps = {
  isEditing?: boolean;
  draftCombos: PortComboDraft[];
  onDraftCombosChange: (draftCombos: PortComboDraft[]) => void;
};

export function PortCombosTable({ isEditing = false, draftCombos }: PortCombosTableProps) {
  const { sortedRows, handleSort, sortState } = useStructureTableSort<PortComboSortKey>(
    draftCombos,
    { key: "index", direction: "asc" },
  );

  return (
    <div className="m-0">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[120px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("name")}
              >
                Name <SortIcon sortState={sortState} key="name" />
              </button>
            </TableHead>
            <TableHead className="w-[80px] text-right">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("index")}
              >
                Index <SortIcon sortState={sortState} key="index" />
              </button>
            </TableHead>
            <TableHead className="w-[150px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("inputs")}
              >
                Inputs <SortIcon sortState={sortState} key="inputs" />
              </button>
            </TableHead>
            <TableHead className="w-[150px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("outputs")}
              >
                Outputs <SortIcon sortState={sortState} key="outputs" />
              </button>
            </TableHead>
            <TableHead className="w-[150px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("pads")}
              >
                Pads <SortIcon sortState={sortState} key="pads" />
              </button>
            </TableHead>
            <TableHead className="w-[150px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("settings")}
              >
                Settings <SortIcon sortState={sortState} key="settings" />
              </button>
            </TableHead>
            <TableHead className="w-[120px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("alignAtPeak")}
              >
                Align At Peak <SortIcon sortState={sortState} key="alignAtPeak" />
              </button>
            </TableHead>
            <TableHead className="w-[120px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("alignPwrRng")}
              >
                Align Pwr Rng <SortIcon sortState={sortState} key="alignPwrRng" />
              </button>
            </TableHead>
            <TableHead className="w-[120px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("measurePwrRng")}
              >
                Measure Pwr Rng <SortIcon sortState={sortState} key="measurePwrRng" />
              </button>
            </TableHead>
            <TableHead className="w-[120px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("zMargin")}
              >
                Z Margin <SortIcon sortState={sortState} key="zMargin" />
              </button>
            </TableHead>
            <TableHead className="w-[120px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("probeCheck")}
              >
                Probe Check <SortIcon sortState={sortState} key="probeCheck" />
              </button>
            </TableHead>
            <TableHead className="w-[120px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("blindStep")}
              >
                Blind Step <SortIcon sortState={sortState} key="blindStep" />
              </button>
            </TableHead>
            <TableHead className="w-[120px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("dynamicAlignment")}
              >
                Dynamic Alignment <SortIcon sortState={sortState} key="dynamicAlignment" />
              </button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.length > 0 ? (
            sortedRows.map((combo) => (
              <TableRow key={combo.id}>
                <TableCell className="w-[120px]">
                  <DesignTableCell value={combo.name} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[80px] text-right">
                  <DesignTableCell
                    value={combo.index}
                    isEditing={isEditing}
                    inputType="number"
                    inputClassName="h-8 w-full text-right"
                  />
                </TableCell>
                <TableCell className="w-[150px] align-top">
                  <DesignTableCell
                    value={isEditing ? parseSelections(combo.inputs) : combo.inputs}
                    isEditing={isEditing}
                    inputType="multiple"
                    inputClassName="h-8 w-full"
                  />
                </TableCell>
                <TableCell className="w-[150px] align-top">
                  <DesignTableCell
                    value={isEditing ? parseSelections(combo.outputs) : combo.outputs}
                    isEditing={isEditing}
                    inputType="multiple"
                    inputClassName="h-8 w-full"
                  />
                </TableCell>
                <TableCell className="w-[150px] align-top">
                  <DesignTableCell
                    value={isEditing ? parseSelections(combo.pads) : combo.pads}
                    isEditing={isEditing}
                    inputType="multiple"
                    inputClassName="h-8 w-full"
                  />
                </TableCell>
                <TableCell className="w-[150px] align-top">
                  <DesignTableCell
                    inputType="multiple"
                    inputClassName="h-8 w-full"
                    value={combo.settings}
                    isEditing={isEditing}
                  />
                </TableCell>
                <TableCell className="w-[140px]">
                  <DesignTableCell value={combo.alignAtPeak} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[150px]">
                  <DesignTableCell value={combo.alignPwrRng} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[120px]">
                  <DesignTableCell value={combo.measurePwrRng} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[120px]">
                  <DesignTableCell value={combo.zMargin} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[120px]">
                  <DesignTableCell value={combo.probeCheck} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[120px]">
                  <DesignTableCell value={combo.blindStep} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[120px]">
                  <DesignTableCell value={combo.dynamicAlignment} isEditing={isEditing} />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                No port combos available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
